import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { PlayFormat } from '../../../services/AnalyticsService/Types';
import { PlayerVideoRef } from '../components/PlayerVideo/PlayerVideo.types';
import { PlayerEvents } from '../playbackTypes';
import { MEDIA_TYPE } from '../types';
import { sendPlayEvent, sendPlays } from '../utils/analytics';
import { checkForCanPlayEventBug } from '../utils/checkForCanPlayEventBug';

const appDebug = require('debug')('tca:KitPlayer:hooks:usePlayerVideoEvents');

export interface UsePlayerVideoEventsResponse {
  bufferedTimeSeconds: number;
  canPlay: boolean;
  currentMediaType: MEDIA_TYPE;
  hasEnded: boolean;
  isLoading: boolean;
  isPlayerReady: boolean;
  isPlaying: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setIsPlaying: (isPlaying: boolean) => void;
  totalDurationSeconds?: number;
  hasCanPlayEventBug?: boolean;

  onDurationChange: (duration: number) => void;
  onVideoBufferEnd: () => void;
  onVideoBufferStart: () => void;
  onVideoCanplay: () => void;
  onVideoEnd: () => void;
  onVideoError: (e: { error: unknown }) => void;
  onVideoLoadStart: () => void;
  onVideoPause: () => void;
  onVideoPlay: () => void;
  onVideoProgress: (data: { currentSeconds: number }) => void;
  onVideoReady: (data: { duration: number }) => void;
}

/**
 * Hook to handle player video events
 */
export default ({
  analyticsFormat,
  appKey = '',
  autoPlay,
  currentMediaType,
  isLive,
  isSeeking,
  mediaItemShortCode,
  playerVideoRef,
  playId = '',
  url,

  onPause,
  onPlay,
  onProgress,
}: {
  analyticsFormat: PlayFormat;
  appKey?: string;
  autoPlay: boolean;
  currentMediaType: MEDIA_TYPE;
  isLive: boolean;
  isSeeking: boolean;
  mediaItemShortCode?: string;
  playerVideoRef: React.RefObject<PlayerVideoRef | null>;
  playId?: string;
  url: string;

  onPause?: () => void;
  onPlay?: () => void;
  onProgress?: (currentTime: number) => void;
}): UsePlayerVideoEventsResponse => {
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [bufferedTimeSeconds, setBufferedTimeSeconds] = useState(0);
  const [hasFirstPlay, setHasFirstPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isLoading, setIsLoading] = useState(false);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState<number>(0);
  const [hasEnded, setHasEnded] = useState(false);
  const [canPlay, setCanPlay] = useState(false);

  const canPlayRef = useRef(false);

  const lastKnownTimeRef = useRef(
    playerVideoRef.current?.getCurrentTimeInSeconds() ?? -1
  );

  const hasCanPlayEventBug = useMemo(
    () => checkForCanPlayEventBug(currentMediaType),
    [currentMediaType]
  );

  const updateCanPlay = useCallback((ready: boolean) => {
    canPlayRef.current = ready;
    setCanPlay(ready);
  }, []);

  const sendInitialPlayAnalytics = useCallback(() => {
    if (!appKey || !playId || !url || !analyticsFormat) return;

    sendPlays({
      appKey,
      format: analyticsFormat,
      sapId: mediaItemShortCode,
      playId,
      url,
    });
  }, [analyticsFormat, appKey, mediaItemShortCode, url, playId]);

  const sendAnalyticsPlayEvent = useCallback(
    (event: PlayerEvents) => {
      if (!event || !playId || !appKey) return;

      sendPlayEvent({
        appKey,
        position: playerVideoRef.current?.getCurrentTimeInSeconds() ?? -1,
        event,
        format: analyticsFormat,
        playId,
      });
    },
    [analyticsFormat, appKey, playId, playerVideoRef]
  );

  const onDurationChange = useCallback((duration: number) => {
    setTotalDurationSeconds(duration);
  }, []);

  const onVideoBufferEnd = useCallback(() => {
    if (Platform.OS === 'web') appDebug('onVideoBufferEnd');

    const canPlay = canPlayRef.current;

    /**
     *  do not wait for canplay event if
     *  - can play bug is detected or
     *  - is not live
     *
     * Note:
     *  - Although ios native hls video playback does not have canplay event
     * bug for VOD (non-live streams), we can just ignore canplay event for
     * VOD since its not needed and also just to be safe.
     */
    const readyToPlay = !isLive || canPlay || hasCanPlayEventBug;

    if (readyToPlay) setIsLoading(false);
  }, [setIsLoading, hasCanPlayEventBug, isLive]);

  const onVideoBufferStart = useCallback(() => {
    appDebug('onVideoBufferStart');
    setIsLoading(true);
  }, [setIsLoading]);

  const onVideoEnd = useCallback(() => {
    sendAnalyticsPlayEvent(PlayerEvents.ended);
    setIsPlaying(false);
    setHasEnded(true);
  }, [sendAnalyticsPlayEvent]);

  const onVideoError = useCallback((e: { error: unknown }) => {
    const err = (e?.error ?? e) as Error;

    appDebug('onError', err);
    if (err?.message?.includes('play()')) {
      setIsPlaying(false);
    }
  }, []);

  const onVideoPause = useCallback(() => {
    if (isLoading) {
      setIsLoading(false);
    }

    setIsPlaying(false);

    if (isSeeking) {
      return appDebug('Prevent sending paused analytics while seeking');
    }

    onPause?.();
    sendAnalyticsPlayEvent(PlayerEvents.pause);
  }, [isSeeking, onPause, sendAnalyticsPlayEvent, isLoading]);

  const onVideoCanplay = useCallback(() => {
    appDebug('onVideoCanplay');
    const canPlay = canPlayRef.current;

    if (!canPlay) {
      updateCanPlay(true);
      setIsLoading(false);
    }
  }, [updateCanPlay]);

  const onVideoPlay = useCallback(() => {
    if (!hasFirstPlay) {
      setHasFirstPlay(true);
      sendInitialPlayAnalytics();
    }

    appDebug('onVideoPlay');

    setIsPlaying(true);
    setHasEnded(false);
    if (isSeeking) {
      return appDebug('Prevent sending play analytics while seeking');
    }

    onPlay?.();
    sendAnalyticsPlayEvent(PlayerEvents.play);
  }, [
    hasFirstPlay,
    isSeeking,
    onPlay,
    sendInitialPlayAnalytics,
    sendAnalyticsPlayEvent,
  ]);

  const onVideoProgress = useCallback(
    ({ currentSeconds }: { currentSeconds: number }) => {
      // Update buffered time
      const bufferedTime = playerVideoRef.current?.getSecondsLoaded();

      if (bufferedTime !== undefined) {
        setBufferedTimeSeconds(Math.floor(bufferedTime));
      }

      // Update last known time from progress event
      lastKnownTimeRef.current = currentSeconds ?? -1;

      onProgress?.(currentSeconds);
    },
    [onProgress, playerVideoRef]
  );

  const onVideoLoadStart = useCallback(() => {
    appDebug('onVideoLoadStart');
    setIsPlayerReady(false);
  }, []);

  const onVideoReady = useCallback(
    ({ duration }: { duration: number }) => {
      setIsPlayerReady(true);
      setTotalDurationSeconds(duration);
      // Use lastKnownTimeRef to maintain time across media type changes
      if (lastKnownTimeRef.current > 0) {
        playerVideoRef.current?.seekTo(lastKnownTimeRef.current);
      }
    },
    [playerVideoRef]
  );

  // When current media type changes, seek to the last known time
  useEffect(() => {
    if (playerVideoRef.current && lastKnownTimeRef.current > 0) {
      playerVideoRef.current.seekTo(lastKnownTimeRef.current);
    }
  }, [currentMediaType, playerVideoRef]);

  return {
    bufferedTimeSeconds,
    currentMediaType,
    canPlay,
    hasEnded,
    isLoading,
    isPlayerReady,
    isPlaying,
    setIsLoading,
    setIsPlaying,
    totalDurationSeconds,

    onDurationChange,
    onVideoBufferEnd,
    onVideoBufferStart,
    onVideoCanplay,
    onVideoEnd,
    onVideoError,
    onVideoLoadStart,
    onVideoPause,
    onVideoPlay,
    onVideoProgress,
    onVideoReady,
  };
};
